import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import { Session, User } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import { UserProfile } from '../types'

interface AuthContextType {
  user: User | null
  profile: UserProfile | null
  session: Session | null
  loading: boolean
  profileLoading: boolean
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>
  signUp: (email: string, password: string, name: string) => Promise<{ error: Error | null }>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)
  const profileFetches = useRef(0)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setUser(session?.user ?? null)
      if (session?.user) {
        fetchProfile(session.user.id)
      }
      setLoading(false)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session)
      setUser(session?.user ?? null)
      if (session?.user) {
        fetchProfile(session.user.id)
      } else {
        setProfile(null)
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  const fetchProfile = async (userId: string) => {
    profileFetches.current += 1
    setProfileLoading(true)
    try {
      let retries = 0
      let data = null
      let error = null

      while (retries < 3) {
        const result = await supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .single()

        data = result.data
        error = result.error

        if (!error || retries === 2) break

        await new Promise(resolve => setTimeout(resolve, 500))
        retries++
      }

      if (error) {
        const { data: { user } } = await supabase.auth.getUser()
        if (user) {
          const pendingName = localStorage.getItem(`pending_name_${user.id}`) || user.email?.split('@')[0] || 'User'

          const { data: newProfile, error: insertError } = await supabase
            .from('profiles')
            .insert({
              id: user.id,
              email: user.email || '',
              name: pendingName
            })
            .select()
            .single()

          if (!insertError && newProfile) {
            // Clear pending data from localStorage
            localStorage.removeItem(`pending_name_${user.id}`)

            setProfile({
              id: newProfile.id,
              email: newProfile.email,
              name: newProfile.name,
              role: newProfile.role,
              avatarUrl: newProfile.avatar_url
            })
          }
        }
      } else {
        setProfile({
          id: data.id,
          email: data.email,
          name: data.name,
          role: data.role,
          avatarUrl: data.avatar_url
        })
      }
    } catch (error) {
      console.error('Error fetching profile:', error)
    } finally {
      profileFetches.current -= 1
      if (profileFetches.current === 0) setProfileLoading(false)
    }
  }

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (error) throw error
      return { error: null }
    } catch (error) {
      return { error: error as Error }
    }
  }

  const signUp = async (email: string, password: string, name: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name: name
          },
          emailRedirectTo: `${window.location.origin}/dashboard`
        }
      })

      if (error) throw error

      if (data.user) {
        // Wait a moment for any database operations
        await new Promise(resolve => setTimeout(resolve, 500))

        // Always try to create/update profile
        const { error: upsertError } = await supabase
          .from('profiles')
          .upsert({
            id: data.user.id,
            email: email,
            name: name
          }, {
            onConflict: 'id'
          })

        if (upsertError) {
          console.error('Error creating/updating profile:', upsertError)

          // Try insert then update as fallback
          const { error: insertError } = await supabase
            .from('profiles')
            .insert({
              id: data.user.id,
              email: email,
              name: name
            })

          if (insertError) {
            // If insert fails, try to update
            if (insertError.code === '23505' || insertError.message?.includes('duplicate')) {
              const { error: updateError } = await supabase
                .from('profiles')
                .update({
                  name: name,
                  email: email
                })
                .eq('id', data.user.id)

              if (updateError) {
                console.error('Error updating profile:', updateError)
                // Store in localStorage as last resort
                localStorage.setItem(`pending_name_${data.user.id}`, name)
              }
            } else {
              // Store in localStorage for RLS errors
              localStorage.setItem(`pending_name_${data.user.id}`, name)
            }
          }
        }

        // Call the database function to ensure profile exists
        const { error: fnError } = await supabase.rpc('create_profile_for_user', {
          user_id: data.user.id,
          user_email: email,
          user_name: name
        })

        if (fnError) {
          console.log('Function call error (may be normal if function does not exist):', fnError)
        }
      }

      return { error: null }
    } catch (error) {
      return { error: error as Error }
    }
  }

  const signOut = async () => {
    await supabase.auth.signOut()
  }

  const value = {
    user,
    profile,
    session,
    loading,
    profileLoading,
    signIn,
    signUp,
    signOut,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}